import { CloseSmall, UploadBlue } from '@shared/assets/icons/components';
import { Attachments, type AttachmentsListRef } from '@shared/ui/attachments';
import { authSelectors } from '@shared/redux/ducks/auth';
import { useAppSelector } from '@shared/redux';
import {
  useKnowledgeLinkAddKnowledgeLinkAddPostMutation,
  useKnowledgeLinkRelatedKnowledgeLinkRelatedPostMutation,
  useKnowledgeLinkedDocumentUploadKnowledgeLinkedDocumentUploadPostMutation,
  useKnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePostMutation,
  type KnowledgeSyncTimeEnum,
} from '@shared/api';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Checkbox, Flex, Input, Modal, Select, Tabs, Tooltip, type InputRef } from 'antd';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import styles from './create-modal.module.scss';
import { Text } from '@shared/ui/antd';
import { useTranslation } from 'react-i18next';
import { ScrollContainer } from '@shared/ui/scroll-container';
import {
  hasKnowledgeLinkProtocol,
  parseKnowledgeLink,
} from '@modules/knowledge-base/lib/parse-link';
import { KNOWLEDGE_SYNC_PERIODS } from '@modules/knowledge-base/lib/sync-periods';

interface KnowledgeBaseCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type KnowledgeBaseSourceType = 'document' | 'link' | 'linked_document';
type RelatedKnowledgeLink = {
  url: string;
  title: string;
};

const formatKnowledgeLinkForDisplay = (value: string): string => {
  const parsedLink = parseKnowledgeLink(value);
  if (!parsedLink) {
    return value.replace(/^https?:\/\//i, '');
  }

  try {
    const parsedUrl = new URL(parsedLink.url);
    const path = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;

    return `${parsedUrl.hostname}${path === '/' ? '' : path}`;
  } catch {
    return parsedLink.url.replace(/^https?:\/\//i, '');
  }
};

const getUniqueLinks = (links: string[]): string[] => {
  const uniqueLinks = new Set<string>();

  for (const link of links) {
    const parsedLink = parseKnowledgeLink(link);
    if (!parsedLink) continue;
    uniqueLinks.add(parsedLink.url);
  }

  return [...uniqueLinks];
};

const getUniqueRelatedLinks = (
  links: Array<{ url: string; title?: string }>,
): RelatedKnowledgeLink[] => {
  const uniqueLinks = new Map<string, RelatedKnowledgeLink>();

  for (const link of links) {
    const parsedLink = parseKnowledgeLink(link.url);
    if (!parsedLink) continue;

    const normalizedTitle = link.title?.trim() || parsedLink.host;

    if (!uniqueLinks.has(parsedLink.url)) {
      uniqueLinks.set(parsedLink.url, {
        url: parsedLink.url,
        title: normalizedTitle,
      });
      continue;
    }

    const previousLink = uniqueLinks.get(parsedLink.url);
    if (previousLink && !previousLink.title && normalizedTitle) {
      uniqueLinks.set(parsedLink.url, {
        ...previousLink,
        title: normalizedTitle,
      });
    }
  }

  return [...uniqueLinks.values()];
};

export const KnowledgeBaseCreateModal: FC<KnowledgeBaseCreateModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'knowledgeBase.createModal' });
  const { t: tKnowledgeBase } = useTranslation('translation', { keyPrefix: 'knowledgeBase' });
  const organizationId = useAppSelector(authSelectors.selectCurrentOrganizationId);
  const [sourceType, setSourceType] = useState<KnowledgeBaseSourceType>('document');
  const [attachmentsState, setAttachmentsState] = useState({
    fileCount: 0,
    isFormInvalid: false,
  });
  const [link, setLink] = useState('');
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [isSavingLinks, setIsSavingLinks] = useState(false);
  const [relatedLinks, setRelatedLinks] = useState<RelatedKnowledgeLink[]>([]);
  const [selectedRelatedLinks, setSelectedRelatedLinks] = useState<string[]>([]);
  const [linkedDocument, setLinkedDocument] = useState({
    url: '',
    syncPeriod: 'EVERY_DAY' as KnowledgeSyncTimeEnum,
    title: '',
    description: '',
  });
  const [isSavingLinkedDocument, setIsSavingLinkedDocument] = useState(false);
  const [addKnowledgeLink, { isLoading: isLinkUploading }] =
    useKnowledgeLinkAddKnowledgeLinkAddPostMutation();
  const [getRelatedLinks, { isLoading: isRelatedLinksLoading }] =
    useKnowledgeLinkRelatedKnowledgeLinkRelatedPostMutation();
  const [addLinkedDocument, { isLoading: isLinkedDocumentUploading }] =
    useKnowledgeLinkedDocumentUploadKnowledgeLinkedDocumentUploadPostMutation();
  const [updateSyncPeriod, { isLoading: isSyncPeriodUpdating }] =
    useKnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePostMutation();
  const attachmentsListRef = useRef<AttachmentsListRef>(null);
  const linkInputRef = useRef<InputRef>(null);
  const parsedLink = useMemo(() => parseKnowledgeLink(link), [link]);
  const parsedLinkedDocumentUrl = useMemo(
    () => parseKnowledgeLink(linkedDocument.url),
    [linkedDocument.url],
  );
  const showHttpsAddon = !hasKnowledgeLinkProtocol(link);
  const showLinkedDocumentHttpsAddon = !hasKnowledgeLinkProtocol(linkedDocument.url);
  const previousShowHttpsAddonRef = useRef(showHttpsAddon);
  const isSaveDisabled = attachmentsState.fileCount === 0 || attachmentsState.isFormInvalid;
  const isLinkInvalid = link.trim().length > 0 && !parsedLink;
  const isLinkSaveDisabled = !parsedLink || !organizationId || isSavingLinks;
  const isLinkedDocumentUrlInvalid =
    linkedDocument.url.trim().length > 0 && !parsedLinkedDocumentUrl;
  const isLinkedDocumentSaveDisabled =
    !parsedLinkedDocumentUrl ||
    !organizationId ||
    !linkedDocument.title.trim() ||
    isSavingLinkedDocument;
  const relatedLinksUrls = useMemo(
    () => relatedLinks.map((relatedLink) => relatedLink.url),
    [relatedLinks],
  );
  const relatedLinksUrlsSet = useMemo(() => new Set(relatedLinksUrls), [relatedLinksUrls]);
  const selectedRelatedLinksSet = useMemo(
    () => new Set(selectedRelatedLinks),
    [selectedRelatedLinks],
  );
  const syncPeriodOptions = useMemo(
    () =>
      KNOWLEDGE_SYNC_PERIODS.map((syncPeriod) => ({
        value: syncPeriod,
        label: tKnowledgeBase(`syncPeriods.${syncPeriod}`),
      })),
    [tKnowledgeBase],
  );
  const isSelectAllDisabled =
    relatedLinks.length === 0 || selectedRelatedLinks.length === relatedLinks.length;

  const resetState = () => {
    setSourceType('document');
    setLink('');
    setRelatedLinks([]);
    setSelectedRelatedLinks([]);
    setIsSavingLinks(false);
    setLinkedDocument({
      url: '',
      syncPeriod: 'EVERY_DAY',
      title: '',
      description: '',
    });
    setIsSavingLinkedDocument(false);
    setAttachmentsState({
      fileCount: 0,
      isFormInvalid: false,
    });
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  useEffect(() => {
    const previousShowHttpsAddon = previousShowHttpsAddonRef.current;

    if (sourceType === 'link' && previousShowHttpsAddon !== showHttpsAddon) {
      linkInputRef.current?.focus({ cursor: 'end' });
    }

    previousShowHttpsAddonRef.current = showHttpsAddon;
  }, [showHttpsAddon, sourceType]);

  useEffect(() => {
    setSelectedRelatedLinks((previousSelectedLinks) => {
      if (!parsedLink) {
        return previousSelectedLinks.length === 0 ? previousSelectedLinks : [];
      }

      const nextSelectedLinks = previousSelectedLinks.filter(
        (url) => url === parsedLink.url || relatedLinksUrlsSet.has(url),
      );

      const isSameSelection =
        nextSelectedLinks.length === previousSelectedLinks.length &&
        nextSelectedLinks.every((url, index) => url === previousSelectedLinks[index]);

      return isSameSelection ? previousSelectedLinks : nextSelectedLinks;
    });
  }, [parsedLink, relatedLinksUrlsSet]);

  const handleSaveFile = async () => {
    if (!attachmentsListRef.current) return;
    setIsUploadingFiles(true);
    try {
      await attachmentsListRef.current.uploadAll();
      handleClose();
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleExtractRelatedLinks = async () => {
    if (!parsedLink) return;

    const response = await getRelatedLinks({
      bodyKnowledgeLinkRelatedKnowledgeLinkRelatedPost: {
        url: parsedLink.url,
      },
    }).unwrap();

    const normalizedLinks = getUniqueRelatedLinks([
      { url: parsedLink.url, title: parsedLink.host },
      ...response.map((relatedLink) => ({
        url: relatedLink.link,
        title: relatedLink.title,
      })),
    ]);

    setRelatedLinks(normalizedLinks);
    setSelectedRelatedLinks([parsedLink.url]);
  };

  const handleToggleRelatedLink = (url: string) => {
    setSelectedRelatedLinks((previousSelectedLinks) =>
      previousSelectedLinks.includes(url)
        ? previousSelectedLinks.filter((selectedUrl) => selectedUrl !== url)
        : [...previousSelectedLinks, url],
    );
  };

  const handleSelectAllRelatedLinks = () => {
    if (isSelectAllDisabled) return;
    setSelectedRelatedLinks(relatedLinksUrls);
  };

  const handleSaveLink = async () => {
    if (!parsedLink || !organizationId) return;

    const linksToUpload = getUniqueLinks([parsedLink.url, ...selectedRelatedLinks]);

    setIsSavingLinks(true);
    try {
      await Promise.all(
        linksToUpload.map((linkUrl) => {
          const parsedUploadLink = parseKnowledgeLink(linkUrl);
          if (!parsedUploadLink) return undefined;

          return addKnowledgeLink({
            bodyKnowledgeLinkAddKnowledgeLinkAddPost: {
              organization_id: Number(organizationId),
              url: parsedUploadLink.url,
              title: parsedUploadLink.host,
              description: parsedUploadLink.url,
            },
          }).unwrap();
        }),
      );

      handleClose();
    } finally {
      setIsSavingLinks(false);
    }
  };

  const handleLinkedDocumentChange = (
    field: keyof typeof linkedDocument,
    value: string | KnowledgeSyncTimeEnum,
  ) => {
    setLinkedDocument((previousLinkedDocument) => ({
      ...previousLinkedDocument,
      [field]: value,
    }));
  };

  const handleSaveLinkedDocument = async () => {
    if (!parsedLinkedDocumentUrl || !organizationId) return;

    const title = linkedDocument.title.trim();
    const description = linkedDocument.description.trim();

    if (!title) return;

    setIsSavingLinkedDocument(true);
    try {
      const createdKnowledgeBase = await addLinkedDocument({
        bodyKnowledgeLinkedDocumentUploadKnowledgeLinkedDocumentUploadPost: {
          organization_id: Number(organizationId),
          url: parsedLinkedDocumentUrl.url,
          title,
          description,
        },
      }).unwrap();

      await updateSyncPeriod({
        bodyKnowledgeSyncPeriodUpdateKnowledgeSyncPeriodUpdatePost: {
          knowledge_base_id: createdKnowledgeBase.id,
          sync_period: linkedDocument.syncPeriod,
        },
      }).unwrap();

      handleClose();
    } finally {
      setIsSavingLinkedDocument(false);
    }
  };

  return (
    <>
      <Modal
        centered
        open={isOpen}
        onCancel={handleClose}
        destroyOnClose
        width={480}
        styles={{
          content: {
            background: 'var(--color-bg-container)',
            border: '1px solid var(--color-border-soft)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-dropdown)',
          },
          body: {
            background: 'transparent',
          },
        }}
        cancelButtonProps={{ hidden: true }}
        okButtonProps={{ hidden: true }}
        title={null}
        closeIcon={<CloseSmall />}
      >
        <Flex vertical gap={16} className={styles.root}>
          <Flex vertical gap={8} className={styles.header}>
            <Text variant="md" fontWeight={500} className={styles.title}>
              {t('title')}
            </Text>
            <Text variant="sm" className={styles.subtitle}>
              {t('subtitle')}
            </Text>
          </Flex>

          <Tabs
            activeKey={sourceType}
            onChange={(key) => setSourceType(key as KnowledgeBaseSourceType)}
            className={styles.tabs}
            items={[
              { key: 'document', label: t('tabs.document') },
              { key: 'link', label: t('tabs.link') },
              { key: 'linked_document', label: t('tabs.linkedDocument') },
            ]}
          />

          {sourceType === 'document' && (
            <>
              <Text variant="sm" className={styles.limit}>
                {t('limit')}
              </Text>

              <Attachments.Root>
                <div className={styles.attachments}>
                  <Attachments.UploadInput
                    variant="knowledgeBase"
                    icon={<UploadBlue />}
                    title={t('attachTitle')}
                    subtitle={t('attachSubtitle')}
                    maxFiles={5}
                  />
                  <Attachments.List
                    ref={attachmentsListRef}
                    onStateChange={setAttachmentsState}
                    isUploadButtonHidden
                    className={styles.attachmentsList}
                  />
                </div>
              </Attachments.Root>
            </>
          )}

          {sourceType === 'link' && (
            <Flex vertical gap={16} className={styles.linkSection}>
              <Input
                ref={linkInputRef}
                value={link}
                onChange={(event) => {
                  setLink(event.target.value);
                  setRelatedLinks([]);
                  setSelectedRelatedLinks([]);
                }}
                placeholder={t('linkPlaceholder')}
                className={styles.linkInput}
                addonBefore={showHttpsAddon ? 'https://' : undefined}
                status={isLinkInvalid ? 'error' : undefined}
              />
              {isLinkInvalid && (
                <Text variant="xs" className={styles.linkError}>
                  {t('linkInvalid')}
                </Text>
              )}
              <Button
                className={styles.extractButton}
                onClick={handleExtractRelatedLinks}
                loading={isRelatedLinksLoading}
                disabled={!parsedLink || isRelatedLinksLoading}
              >
                {t('extractButton')}
              </Button>

              {relatedLinks.length > 0 && (
                <Flex vertical gap={8} className={styles.relatedLinksSection}>
                  <Flex
                    align="center"
                    justify="space-between"
                    className={styles.relatedLinksHeader}
                  >
                    <Text variant="md" fontWeight={500}>
                      {t('relatedLinksTitle')}
                    </Text>
                    <Button
                      className={styles.selectAllButton}
                      onClick={handleSelectAllRelatedLinks}
                      disabled={isSelectAllDisabled}
                    >
                      {t('selectAll')}
                    </Button>
                  </Flex>

                  <ScrollContainer
                    height="auto"
                    maxHeight="350px"
                    marginRight="0"
                    style={{ flex: '0 0 auto', minHeight: 'auto' }}
                  >
                    <Flex vertical className={styles.relatedLinksList}>
                      {relatedLinks.map((relatedLink) => (
                        <label key={relatedLink.url} className={styles.relatedLinkItem}>
                          <Checkbox
                            checked={selectedRelatedLinksSet.has(relatedLink.url)}
                            onChange={() => handleToggleRelatedLink(relatedLink.url)}
                          />
                          <span className={styles.relatedLinkContent}>
                            <Text variant="sm" fontWeight={400} hasTooltip maxLength={50}>
                              {relatedLink.title}
                            </Text>

                            <Text
                              variant="sm"
                              fontWeight={400}
                              hasTooltip
                              maxLength={50}
                              className={styles.relatedLinkText}
                            >
                              {formatKnowledgeLinkForDisplay(relatedLink.url)}
                            </Text>
                          </span>
                        </label>
                      ))}
                    </Flex>
                  </ScrollContainer>
                </Flex>
              )}
            </Flex>
          )}

          {sourceType === 'linked_document' && (
            <Flex vertical gap={16} className={styles.linkSection}>
              <Flex vertical gap={6}>
                <Text variant="sm" fontWeight={500} className={styles.fieldLabel}>
                  {t('linkedDocument.urlLabel')}
                  <Tooltip title={t('linkedDocument.urlTooltip')}>
                    <QuestionCircleOutlined className={styles.tooltipIcon} />
                  </Tooltip>
                </Text>
                <Input
                  value={linkedDocument.url}
                  onChange={(event) => handleLinkedDocumentChange('url', event.target.value)}
                  placeholder={t('linkedDocument.urlPlaceholder')}
                  className={styles.linkInput}
                  addonBefore={showLinkedDocumentHttpsAddon ? 'https://' : undefined}
                  status={isLinkedDocumentUrlInvalid ? 'error' : undefined}
                />
                {isLinkedDocumentUrlInvalid && (
                  <Text variant="xs" className={styles.linkError}>
                    {t('linkInvalid')}
                  </Text>
                )}
              </Flex>

              <Flex vertical gap={6}>
                <Text variant="sm" fontWeight={500} className={styles.fieldLabel}>
                  {t('linkedDocument.syncPeriodLabel')}
                </Text>
                <Select
                  value={linkedDocument.syncPeriod}
                  options={syncPeriodOptions}
                  onChange={(value) => handleLinkedDocumentChange('syncPeriod', value)}
                  className={styles.syncPeriodSelect}
                />
              </Flex>

              <Flex vertical gap={6}>
                <Text variant="sm" fontWeight={500} className={styles.fieldLabel}>
                  {t('linkedDocument.titleLabel')}
                </Text>
                <Input
                  value={linkedDocument.title}
                  onChange={(event) => handleLinkedDocumentChange('title', event.target.value)}
                  placeholder={t('linkedDocument.titlePlaceholder')}
                  className={styles.linkInput}
                />
              </Flex>

              <Flex vertical gap={6}>
                <Text variant="sm" fontWeight={500} className={styles.fieldLabel}>
                  {t('linkedDocument.descriptionLabel')}
                </Text>
                <Input.TextArea
                  value={linkedDocument.description}
                  onChange={(event) =>
                    handleLinkedDocumentChange('description', event.target.value)
                  }
                  placeholder={t('linkedDocument.descriptionPlaceholder')}
                  className={styles.descriptionInput}
                  maxLength={2048}
                  showCount
                  rows={4}
                />
              </Flex>
            </Flex>
          )}

          {sourceType === 'document' && attachmentsState.fileCount > 0 && (
            <Button
              type="primary"
              className={styles.saveButton}
              onClick={handleSaveFile}
              loading={isUploadingFiles}
              disabled={isSaveDisabled || isUploadingFiles}
              block
            >
              {t('button')}
            </Button>
          )}

          {sourceType === 'link' && (
            <Button
              type="primary"
              className={styles.saveButton}
              onClick={handleSaveLink}
              loading={isSavingLinks || isLinkUploading}
              disabled={isLinkSaveDisabled || isLinkUploading}
              block
            >
              {t('button')}
            </Button>
          )}

          {sourceType === 'linked_document' && (
            <Button
              type="primary"
              className={styles.saveButton}
              onClick={handleSaveLinkedDocument}
              loading={isSavingLinkedDocument || isLinkedDocumentUploading || isSyncPeriodUpdating}
              disabled={
                isLinkedDocumentSaveDisabled || isLinkedDocumentUploading || isSyncPeriodUpdating
              }
              block
            >
              {t('button')}
            </Button>
          )}
        </Flex>
      </Modal>
    </>
  );
};
